/**
 * Why a number typed into a setting can't be saved, as the settings page says
 * it (ADR 0172), or null if it can. The API checks each value again; these
 * say the same, before anything is sent.
 */

export interface WholeNumberRule {
  unit: "minutes" | "seconds" | "hours" | "days" | "percent";
  min: number;
  /** None: as large as a whole number can be. */
  max?: number;
  /** Said after the maximum: " (a day)". */
  maxNote?: string;
}

const ONE: Record<WholeNumberRule["unit"], string> = {
  minutes: "minute",
  seconds: "second",
  hours: "hour",
  days: "day",
  percent: "%",
};

function amount(value: number, unit: WholeNumberRule["unit"]): string {
  const number = value.toLocaleString("en-US");
  if (unit === "percent") {
    return `${number}%`;
  }
  return `${number} ${value === 1 ? ONE[unit] : unit}`;
}

/** A whole number of `rule.unit`, from `rule.min` to `rule.max`. */
export function wholeNumberProblem(text: string, rule: WholeNumberRule): string | null {
  const value = text.trim();
  const percent = rule.unit === "percent";
  if (value === "") {
    return percent ? "enter a percentage." : `enter a number of ${rule.unit}.`;
  }
  if (!/^-?\d+$/.test(value)) {
    return percent ? "use a whole percentage." : `use whole ${rule.unit}.`;
  }
  const number = Number(value);
  if (!Number.isSafeInteger(number)) {
    return "use a smaller number.";
  }
  const { min, max, maxNote = "" } = rule;
  if (max !== undefined && min > 0 && (number < min || number > max)) {
    return `it can be ${min.toLocaleString("en-US")} to ${amount(max, rule.unit)}${maxNote}.`;
  }
  if (number < min) {
    return `it can't be less than ${min === 0 ? "0" : amount(min, rule.unit)}.`;
  }
  if (max !== undefined && number > max) {
    return `it can be at most ${amount(max, rule.unit)}${maxNote}.`;
  }
  return null;
}
